import { memo, useCallback, useEffect, useId, useRef, useState } from 'react';
import Checkbox from '../Checkbox.jsx';
import {
  MENU_ITEM_FORCED_ACTIVE_SURFACE,
  MENU_ITEM_HOVER_SURFACE,
  MENU_PANEL_BASE,
  MENU_PANEL_DEFAULT_MAX_HEIGHT_CLASS,
  MENU_PANEL_SCROLL_GUTTER_CLASS,
  MENU_PANEL_SCROLL_VIEWPORT_CLASS,
} from '../Menu/menuTokens.js';
import {
  FILTERS_DROPDOWN_OPTION,
  FILTERS_DROPDOWN_OPTION_SELECTED,
} from './filtersTokens.js';
import { joinClasses } from './menuFilter.utils.js';
import { useAnchoredPosition } from './useAnchoredPosition.js';

/**
 * Anchored listbox for filter slot — multi (checkbox) or single (`selected` row highlight).
 * Always mounted in the DOM; visibility toggles with `invisible + pointer-events-none`
 * so positions/measurements stay cheap and the same nodes are reused across opens.
 * Shell reuses `MenuPanel` tokens (Figma 13483:1642 / 8426:37662).
 *
 * @param {object} props
 * @param {import('./filtersTypes.js').FilterOption[]} props.options
 * @param {string[]} props.value
 * @param {(next: string[]) => void} props.onChange
 * @param {boolean} [props.multiple=true]
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {import('react').RefObject<HTMLElement|null>} props.triggerRef
 * @param {'start'|'end'} [props.align='start']
 * @param {string} [props.listboxId]
 */
const FilterDropdown = ({
  options = [],
  value = [],
  onChange,
  multiple = true,
  open,
  onClose,
  triggerRef,
  align = 'start',
  listboxId: listboxIdProp,
}) => {
  const generatedId = useId();
  const listboxId = listboxIdProp || generatedId;
  const dropdownRef = useRef(null);
  const [focusedIndex, setFocusedIndex] = useState(-1);

  const positionStyle = useAnchoredPosition({
    triggerRef,
    dropdownRef,
    open,
    align,
  });

  const handleToggle = useCallback(
    (optValue) => {
      if (multiple) {
        const next = value.includes(optValue)
          ? value.filter((v) => v !== optValue)
          : [...value, optValue];
        onChange?.(next);
        return;
      }

      onChange?.([optValue]);
      onClose?.();
    },
    [multiple, value, onChange, onClose],
  );

  useEffect(() => {
    if (!open) {
      setFocusedIndex(-1);
      return undefined;
    }

    const onDocMouseDown = (e) => {
      const dropdown = dropdownRef.current;
      const trigger = triggerRef.current;
      if (dropdown?.contains(e.target) || trigger?.contains(e.target)) return;
      onClose?.();
    };

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose?.();
        triggerRef.current?.focus();
        return;
      }

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setFocusedIndex((i) => Math.min(i + 1, options.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setFocusedIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === 'Enter' && focusedIndex >= 0) {
        e.preventDefault();
        const opt = options[focusedIndex];
        if (opt && !opt.disabled) handleToggle(opt.value);
      }
    };

    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onClose, options, focusedIndex, triggerRef, handleToggle]);

  return (
    <div
      ref={dropdownRef}
      id={listboxId}
      role="listbox"
      aria-multiselectable={multiple || undefined}
      aria-hidden={!open || undefined}
      style={positionStyle}
      className={joinClasses(
        MENU_PANEL_BASE,
        'absolute top-full z-[100] mt-1',
        'min-w-[max(100%,12rem)] w-max max-w-[min(28rem,calc(100vw-2rem))]',
        !open && 'invisible pointer-events-none',
      )}
    >
      <div className={MENU_PANEL_SCROLL_GUTTER_CLASS}>
        <ul
          className={joinClasses(
            MENU_PANEL_SCROLL_VIEWPORT_CLASS,
            MENU_PANEL_DEFAULT_MAX_HEIGHT_CLASS,
          )}
        >
          {options.map((opt, index) => {
            const isSelected = value.includes(opt.value);
            const isFocused = index === focusedIndex;

            const optionClass = multiple
              ? joinClasses(
                  FILTERS_DROPDOWN_OPTION,
                  isSelected && FILTERS_DROPDOWN_OPTION_SELECTED,
                  isFocused && 'bg-surface-hover',
                  opt.disabled && 'cursor-not-allowed opacity-50',
                )
              : joinClasses(
                  'flex w-full cursor-pointer items-center px-4 py-2',
                  'text-sm font-medium leading-5 text-content transition-colors',
                  MENU_ITEM_HOVER_SURFACE,
                  (isSelected || isFocused) && MENU_ITEM_FORCED_ACTIVE_SURFACE,
                  opt.disabled && 'cursor-not-allowed opacity-50',
                );

            return (
              <li
                key={opt.value}
                role="option"
                aria-selected={isSelected}
                aria-disabled={opt.disabled || undefined}
                className={optionClass}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  if (!opt.disabled) handleToggle(opt.value);
                }}
                onMouseEnter={() => setFocusedIndex(index)}
              >
                {multiple ? (
                  <span aria-hidden>
                    <Checkbox
                      checked={isSelected}
                      disabled={opt.disabled}
                      size="small"
                      aria-label={opt.label}
                    />
                  </span>
                ) : null}
                <span className="truncate">{opt.label}</span>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

export default memo(FilterDropdown);
